import type { Building } from "building-types/building/buildingTypes";
import type { RoomOutlines } from "building-types/rooms/roomOutlineTypes";
import { RoomOutlineType } from "building-types/rooms/roomOutlineTypes";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { RWTable, Table } from "common/data/table";
import { planarGraphTraceFaceGroupPerimeter } from "geom/planarGraph";
import { polyLine2Copy, polyLine2HasNaN } from "geom/polyLine2";
import type { HalfEdgeWallOffsetsCache } from "../generationTools/generateRoomOutlinesTools";
import {
  polyLine2RoomOutlineByWallLayer,
  polyLine2RoomOutlineByWallLayerIfUniqueCache,
} from "../generationTools/generateRoomOutlinesTools";
import memo from "../utils/memo";

function select({ rooms, wallMasses, roomLevelIndices, planarGraphs }: Building) {
  return {
    rooms,
    roomLevelIndices,
    wallMasses,
    planarGraphs,
  };
}

function generate({ rooms, roomLevelIndices, planarGraphs, wallMasses }: ReturnType<typeof select>): {
  roomOutlineTable: Table<RoomOutlines>;
} {
  const roomOutlineTable: RWTable<RoomOutlines> = {};

  if (!rooms) {
    return { roomOutlineTable };
  }

  for (const roomId in rooms) {
    const room = rooms[roomId];
    const wallMassSystem = wallMasses[room.level.id];
    const polygonPlanarGraph = planarGraphs[room.level.id];
    const planarGraph = polygonPlanarGraph.planarGraph;
    const index = polygonPlanarGraph.polygonToPlanarGraph[roomLevelIndices[roomId]]?.[0]?.[0]?.index;
    const indices = [index];
    const { faceHalfEdges, faceVertices } = planarGraphTraceFaceGroupPerimeter(planarGraph, indices);
    // create outline for INSIDE_OF_MASS_IGNORE_VOID layer
    const wallOffsetCache: HalfEdgeWallOffsetsCache = {};
    const insideOfMassIgnoreVoidPl = polyLine2RoomOutlineByWallLayer({
      wallLayer: WallLayerType.MASS,
      faceHalfEdges,
      face: 0,
      wallMassSystem,
      wallOffsetCache,
      planarGraph,
      faceVertices,
      ignoreVoidLayers: true,
    });

    // fallback to room outline if pl is unusable
    const resolvedInsideOfMassIgnoreVoidPl = polyLine2HasNaN(insideOfMassIgnoreVoidPl)
      ? polyLine2Copy(room.outline)
      : polyLine2Copy(insideOfMassIgnoreVoidPl);
    const roomOutlines: RoomOutlines = {
      [RoomOutlineType.INSIDE_OF_MASS_IGNORE_VOID]: resolvedInsideOfMassIgnoreVoidPl,
    };

    // create outline for the INSIDE_OF_MASS layer (if different from INSIDE_OF_MASS_IGNORE_VOID)
    const { resultingRoomOutline: massPl, resultingWallOffsetCache: massCache } =
      polyLine2RoomOutlineByWallLayerIfUniqueCache({
        wallLayer: WallLayerType.MASS,
        faceHalfEdges,
        face: 0,
        wallMassSystem,
        wallOffsetCache,
        planarGraph,
        faceVertices,
      });

    if (massPl && !polyLine2HasNaN(massPl)) {
      roomOutlines.INSIDE_OF_MASS = polyLine2Copy(massPl);
    } else {
      // fall back to INSIDE_OF_MASS_IGNORE_VOID
      roomOutlines.INSIDE_OF_MASS = roomOutlines.INSIDE_OF_MASS_IGNORE_VOID;
    }

    // create outline for the INSIDE_OF_MASS_MAX_OFFSET layer (if different from INSIDE_OF_MASS_IGNORE_VOID)
    const { resultingRoomOutline: massMaxOffsetPl } = polyLine2RoomOutlineByWallLayerIfUniqueCache({
      wallLayer: WallLayerType.MASS,
      faceHalfEdges,
      face: 0,
      wallMassSystem,
      wallOffsetCache,
      planarGraph,
      faceVertices,
      useMaxWallStripOffset: true,
      ignoreVoidLayers: true, // setting this to true mimics the behavior of wallStrip.baselineMaxOffset
    });

    if (massMaxOffsetPl && !polyLine2HasNaN(massMaxOffsetPl)) {
      roomOutlines.INSIDE_OF_MASS_MAX_OFFSET = polyLine2Copy(massMaxOffsetPl);
    } else {
      // fall back to INSIDE_OF_MASS_IGNORE_VOID
      roomOutlines.INSIDE_OF_MASS_MAX_OFFSET = roomOutlines.INSIDE_OF_MASS_IGNORE_VOID;
    }

    // create outline for the INSIDE_OF_STRUCTURE layer (if different from INSIDE_OF_MASS)
    const { resultingRoomOutline: structureOffsetPl } = polyLine2RoomOutlineByWallLayerIfUniqueCache({
      wallLayer: WallLayerType.STRUCTURE,
      faceHalfEdges,
      face: 0,
      wallMassSystem,
      wallOffsetCache: massCache,
      planarGraph,
      faceVertices,
    });

    if (structureOffsetPl && !polyLine2HasNaN(structureOffsetPl)) {
      roomOutlines.INSIDE_OF_STRUCTURE = polyLine2Copy(structureOffsetPl);
    } else {
      // fall back to INSIDE_OF_MASS
      roomOutlines.INSIDE_OF_STRUCTURE = roomOutlines.INSIDE_OF_MASS;
    }

    // TODO: add outlines for the SUBSTRATE layer once we start generating finish thickness
    // probably don't need an outline for the FINISH layer since the MASS layer outline would be equivalent

    roomOutlineTable[roomId] = roomOutlines;
  }

  return { roomOutlineTable };
}

export default memo(select, generate);
